import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Stops one bad content file from white-screening the whole site.
 *
 * A malformed block or a bad import in `src/content` throws while rendering; in
 * development the message is shown so it can be fixed, and in production the
 * visitor gets a readable page instead of a blank one.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error:', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        style={{
          maxWidth: '40rem',
          margin: '6rem auto',
          padding: '0 1.5rem',
        }}
      >
        <h1 style={{ fontSize: '1.75rem', marginBottom: '1rem' }}>
          Something went wrong
        </h1>
        <p style={{ color: 'var(--c-text-muted)' }}>
          This page failed to render. Reloading may help.
        </p>
        {import.meta.env.DEV && (
          <pre
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              overflowX: 'auto',
              border: '1px solid var(--c-border)',
              borderRadius: '10px',
              background: 'var(--c-bg-raised)',
              fontSize: '0.85rem',
            }}
          >
            {error.stack ?? error.message}
          </pre>
        )}
      </div>
    )
  }
}
